import {Injectable} from '@angular/core';
import {catchError, map, Observable, throwError} from 'rxjs';
import {HttpClient, HttpParams} from '@angular/common/http';
import {TargetEmployeeModel} from '../_models/target-employee.model';
import {Employee} from '../../models/employee/employee.model';
import {Store} from '@ngrx/store';
import {CoreState} from 'src/app/core/store';
import {EmployeeConnectionInfo} from '../../models/employee/employee-connection-info.model';
import {EForceEmployeeModel} from '../_models/eforce-employee.model';
import {EForceRoleModel} from '../_models/eforce-role.model';
import {TargetRoleModel} from '../_models/target-role.model';
import {Role} from '../../models/role/role.model';
import { EmployeeConnectionInfoRequest } from '../../models/employee/employee-connection-info-request.model';
import { IGNORED_ROLE_POPULATIONS } from '../../models/employee/employee-role.enum';

@Injectable({
    providedIn: 'root',
})
export class EmployeeService {
    ORGA_API_URL = '/apiOrga/v1/';

    constructor(
        private store: Store<CoreState>,
        private http: HttpClient) {
    }

    getEmployeeInfo(employeeNumber: string, roleContext?: string): Observable<TargetEmployeeModel | EForceEmployeeModel> {
        let params = new HttpParams().set('employeeNumber', employeeNumber);
        const url = this.ORGA_API_URL + 'employees';
        return this.http.get<EForceEmployeeModel>(url, {params}).pipe(
            map(employeeInfo => this.patchIgnoredPopulationRoleWithRoleFromContext(employeeInfo, roleContext)),
            catchError(err => {
                console.error('Erreur lors de la récupération de l’employé', err);
                err.error?.details && console.log(err.error?.details);
                return throwError(() => err);
            })
        );
    }

    private patchIgnoredPopulationRoleWithRoleFromContext(employeeInfo: EForceEmployeeModel, roleContext?: string): EForceEmployeeModel {
        const ignoredPopulationRole = employeeInfo.roles?.find((role) => IGNORED_ROLE_POPULATIONS.includes(role.code));
        if (ignoredPopulationRole && roleContext) {
            ignoredPopulationRole.code = roleContext;
            ignoredPopulationRole.population = roleContext;
            ignoredPopulationRole.emailAssistant = employeeInfo.email;
            ignoredPopulationRole.emailManager = employeeInfo.email;
        }
        return employeeInfo;
    }

    createEmployeeConnectionInfo(employeeConnectionInfo: EmployeeConnectionInfo): Observable<void> {
        return this.http.post<void>(this.ORGA_API_URL + 'employeeConnectionInfo/target', employeeConnectionInfo);
    }

    saveEmployeeConnectionInfo(employeeConnectionInfoRequest: EmployeeConnectionInfoRequest): Observable<void> {
        return this.http.post<void>(this.ORGA_API_URL + 'employeeConnectionInfo', employeeConnectionInfoRequest);
    }

    mapEmployee(employeeInfo: any): Employee {
        if (employeeInfo.isEforceEmployee) {
            return this.mapEForceEmployee(employeeInfo);
        } else {
            return this.mapTargetEmployee(employeeInfo);
        }
    }

    mapEForceEmployee(employeeInfo: EForceEmployeeModel): Employee {
        const employee = new Employee();
        employee.employeeNumber = employeeInfo.employeeNumber;
        employee.email = employeeInfo.email ? employeeInfo.email : employee.email;
        employee.firstname = employeeInfo.firstName;
        employee.lastname = employeeInfo.lastName;
        employee.phone = employeeInfo.phone ? employeeInfo.phone : employee.phone;
        employee.countryIsoCode = employeeInfo.countryIsoCode;
        employee.locale = employeeInfo.locale;
        employee.isEforceEmployee = true;

        return employee;
    }

    mapTargetEmployee(employeeInfo: TargetEmployeeModel): Employee {
        const employee = new Employee();
        employee.employeeNumber = employeeInfo.employeeNumber;
        employee.email = employeeInfo.email ? employeeInfo.email : employee.email;
        employee.firstname = employeeInfo.firstName;
        employee.lastname = employeeInfo.lastName;
        employee.phone = employeeInfo.phone ? employeeInfo.phone : employee.phone;
        employee.isEforceEmployee = false;
        employee.countryIsoCode = employeeInfo.countryIsoCode;
        if (employeeInfo.employeeConnexionInfo) {
            employee.assistantEmail = employeeInfo.employeeConnexionInfo.assistantEmail;
            employee.managerEmail = employeeInfo.employeeConnexionInfo.managerEmail;
            employee.countryIsoCode = employeeInfo.employeeConnexionInfo.countryIsoCode;
        }

        return employee;
    }


    getAllowedRoles(eligibleRoles: any, employeeInfo: any): Role[] {
        if (employeeInfo.isEforceEmployee) {
            return this.getEForceAllowedRoles(eligibleRoles, employeeInfo.roles);
        } else {
            return this.getTargetAllowedRoles(eligibleRoles, employeeInfo.roles);
        }
    }

    getEForceAllowedRoles(eligibleRoles: Role[], authorizedRoles: EForceRoleModel[]) {
        const roles = [];
        type Team = { teamLabel: string, teamCode: string }
        type Region = { regionCode: string, teams: Team[], regionLabel: string };

        eligibleRoles.forEach(eligibleRole => {
            const filteredRole: EForceRoleModel = authorizedRoles.find((ar) => ar.code === eligibleRole.code.eForceRole);

            if (filteredRole) {
                const team: Team = {teamLabel: filteredRole.team, teamCode: filteredRole.team};

                const region: Region = {
                    regionCode: filteredRole.regionCode,
                    regionLabel: filteredRole.region,
                    teams: [team]
                };

                eligibleRole.regions = [region];
                eligibleRole.assistantEmail = filteredRole.emailAssistant;
                eligibleRole.managerEmail = filteredRole.emailManager;
                roles.push(eligibleRole);
            }

        });
        return roles;
    }

    getTargetAllowedRoles(eligibleRoles: Role[], authorizedRoles: TargetRoleModel[]) {
        const roles = [];
        eligibleRoles.forEach(eligibleRole => {
            const filteredRoles = authorizedRoles.filter((ar) => ar.code === eligibleRole.code.pricingRole);
            if (filteredRoles.length) {
                eligibleRole.regions = filteredRoles[0].regions;
                roles.push(eligibleRole);
            }

        });
        return roles;
    }

}
